/**
 * The module skin, applied **before React renders anything**.
 *
 * ## The bug this exists to fix
 *
 * On an ordinary navigation the development module is blue throughout, because the shell is already
 * mounted and carries `data-module="developing"`. On a **reload** it was green for a moment and then
 * blue: nothing sets `data-module` until `Layout` renders, and `Layout` cannot render until the
 * session has come back from the server. What the reader sees in that gap is the boot spinner —
 * drawn, until now, in the letting green, on a screen that is about to be blue.
 *
 * It reads as the page loading the wrong product and then correcting itself, which is worse than a
 * slow load. So the skin is put on `<html>` synchronously, before the first paint.
 *
 * ## Why this can be answered without a session
 *
 * The module never came from the session in the first place. `useActiveModule` reads, in order: the
 * path, then `?module=`, then what this tab last remembered. All three are available the instant the
 * document exists — the session is consulted only to *narrow* the answer to the modules the
 * organisation actually holds, and that narrowing is what has to wait.
 *
 * So this is a **guess that is right in every case that matters** and self-correcting in the one it
 * is not: `Layout` writes the authoritative value over the top as soon as it knows, so an account
 * that turns out not to hold the development module is blue for the length of one fetch and green
 * thereafter. The alternative — waiting — is green for the length of one fetch on every reload for
 * everybody who does hold it, which is the common case and the one that was reported.
 *
 * ## Only inside the application
 *
 * Nothing outside `/app` is skinned, and `/sign-in` is the reason. `sessionStorage` is per tab and
 * outlives a sign-out, so without the guard somebody who had been in the development module would
 * meet a blue sign-in screen after signing out — a signed-out page belongs to the product, not to
 * a module the reader may no longer have.
 */

import { isModule, moduleForPath, type ModuleCode } from "@rentorng/core";

/** The same key `useActiveModule` remembers under. One name, in one file each — they must agree. */
const KEY = "rentorng.module";

function remembered(): ModuleCode | null {
  try {
    const value = window.sessionStorage.getItem(KEY);
    return isModule(value) ? value : null;
  } catch {
    /* Private mode throws on access rather than returning null. The skin is not worth an exception. */
    return null;
  }
}

/**
 * What the address and this tab's memory say the module is, before anything has been fetched.
 *
 * Exported for the sake of being testable; `applyBootModule` is what the application calls.
 */
export function bootModule(pathname: string, search: string): ModuleCode | null {
  if (pathname !== "/app" && !pathname.startsWith("/app/")) return null;

  const owned = moduleForPath(pathname);
  if (owned) return owned;

  const asked = new URLSearchParams(search).get("module");
  if (isModule(asked)) return asked;

  return remembered();
}

/**
 * Put it on the root element.
 *
 * `<html>` rather than `<body>` or `#root`, so the skin is above absolutely everything — including
 * a dialog or a toast portalled outside the React tree, which is exactly the sort of thing that
 * stays green when the variable is set on a container further in.
 *
 * Letting removes the attribute rather than writing `letting` into it: the letting skin *is* the
 * absence of one, and `[data-module="letting"]` has never been a selector this product defines.
 */
export function applyBootModule(): void {
  const module = bootModule(window.location.pathname, window.location.search);
  if (module && module !== "letting") document.documentElement.dataset.module = module;
  else delete document.documentElement.dataset.module;
}
